import { supabase } from '../supabaseClient';

// Cookie name and expiry
const CONSENT_COOKIE_NAME = 'cookieConsent';
const CONSENT_EXPIRY_DAYS = 365; // 12 months

/**
 * Get current consent from cookie
 */
export const getConsent = () => {
  try {
    const cookie = document.cookie
      .split('; ')
      .find(row => row.startsWith(`${CONSENT_COOKIE_NAME}=`));
    
    if (!cookie) return null;
    
    const value = cookie.split('=')[1];
    return JSON.parse(decodeURIComponent(value));
  } catch (err) {
    console.error('Error reading consent cookie:', err);
    return null;
  }
};

/**
 * Save consent to cookie (12-month expiry)
 */
export const saveConsent = (consent) => {
  try {
    const expiryDate = new Date();
    expiryDate.setDate(expiryDate.getDate() + CONSENT_EXPIRY_DAYS);
    
    const consentData = {
      version: 1,
      timestamp: new Date().toISOString(),
      essential: true, // Always true
      analytics: consent.analytics,
      marketing: consent.marketing,
      method: consent.method
    };
    
    const cookieValue = encodeURIComponent(JSON.stringify(consentData));
    document.cookie = `${CONSENT_COOKIE_NAME}=${cookieValue}; expires=${expiryDate.toUTCString()}; path=/; SameSite=Lax`;
    window.dispatchEvent(new Event("22n:consent-change"));
    
    return consentData;
  } catch (err) {
    console.error('Error saving consent cookie:', err);
    return null;
  }
};

/**
 * Check if user has made a consent choice
 */
export const hasConsentChoice = () => {
  return getConsent() !== null;
};

/**
 * Check if analytics consent is given
 */
export const hasAnalyticsConsent = () => {
  const consent = getConsent();
  return consent?.analytics === true;
};

/**
 * Check if marketing consent is given
 */
export const hasMarketingConsent = () => {
  const consent = getConsent();
  return consent?.marketing === true;
};

/**
 * Clear consent (for testing/reset)
 */
export const clearConsent = () => {
  document.cookie = `${CONSENT_COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
  window.dispatchEvent(new Event("22n:consent-change"));
};

/**
 * Get visitor ID from localStorage (or generate one)
 */
export const getCurrentVisitorId = () => {
  let visitorId = localStorage.getItem('visitorId');
  
  // Generate one if it doesn't exist
  if (!visitorId) {
    visitorId = 'visitor_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem('visitorId', visitorId);
  }
  
  return visitorId;
};

/**
 * Check if user is an existing user (has PageEvent records)
 */
export const checkIsExistingUser = async (userId) => {
  if (!userId) return false;
  
  try {
    const { data, error } = await supabase
      .from('PageEvent')
      .select('id')
      .eq('user_id', userId)
      .limit(1);
    
    if (error) {
      console.error('Error checking existing user:', error);
      return false;
    }
    
    return data && data.length > 0;
  } catch (err) {
    console.error('Error checking existing user:', err);
    return false;
  }
};

/**
 * Save consent to database (for audit trail and cross-device sync)
 * Best effort - doesn't block consent if DB fails
 */
export const saveConsentToDatabase = async (consent, visitorId) => {
  try {
    // Get current user (if logged in)
    const { data: { user } } = await supabase.auth.getUser();
    
    // Ensure we have a visitor ID for anonymous users
    const finalVisitorId = user ? null : (visitorId || getCurrentVisitorId());
    
    const consentRecord = {
      user_id: user?.id || null,
      visitor_id: finalVisitorId,
      analytics_consent: consent.analytics,
      marketing_consent: consent.marketing,
      consent_method: consent.method,
      page_url: window.location.href,
      user_agent: navigator.userAgent,
    };
    
    
    const { error } = await supabase
      .from('cookie_consent')
      .insert(consentRecord);
    
    if (error) {
      console.error('Failed to save consent to database:', error);
      console.error('Consent record was:', consentRecord);
      return null;
    }
    
    return consentRecord;
  } catch (err) {
    console.error('Error saving consent:', err);
    return null;
  }
};

/**
 * Get consent from database (for logged-in users)
 */
export const getConsentFromDatabase = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) return null;
    
    const { data, error } = await supabase
      .from('cookie_consent')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(1);
    
    if (error) {
      console.error('Failed to get consent from database:', error);
      return null;
    }
    
    return data?.[0] ?? null;
  } catch (err) {
    console.error('Error getting consent:', err);
    return null;
  }
};

/**
 * Sync anonymous consent to user account after login
 * Call this when a user logs in to migrate their consent
 */
export const syncConsentOnLogin = async (visitorId) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user || !visitorId) return;
    
    // Check if user already has consent
    const existingConsent = await getConsentFromDatabase();
    if (existingConsent) return; // User already has consent record
    
    // Find anonymous consent by visitor_id
    const { data: anonData } = await supabase
      .from('cookie_consent')
      .select('*')
      .eq('visitor_id', visitorId)
      .is('user_id', null)
      .order('created_at', { ascending: false })
      .limit(1);
    const anonymousConsent = anonData?.[0] ?? null;
    
    if (anonymousConsent) {
      // Create new consent record linked to user
      await supabase
        .from('cookie_consent')
        .insert({
          user_id: user.id,
          visitor_id: null,
          analytics_consent: anonymousConsent.analytics_consent,
          marketing_consent: anonymousConsent.marketing_consent,
          consent_method: anonymousConsent.consent_method,
          page_url: window.location.href,
        });
      
    }
  } catch (err) {
    console.error('Error syncing consent:', err);
  }
};

/**
 * Update existing consent (when user changes preferences)
 * Always inserts a new record for audit trail
 */
export const updateConsentInDatabase = async (consent, visitorId) => {
  return saveConsentToDatabase(consent, visitorId);
};
